const ARABIC_CHARACTER_PATTERN = /\p{Script=Arabic}/gu;
const LATIN_CHARACTER_PATTERN = /\p{Script=Latin}/gu;

const ARABIC_DEGREE_LABELS: Array<{
  pattern: RegExp;
  translation: string;
}> = [
  {
    pattern:
      /^(?:bachelor(?:'s)?(?: degree)?|bachelor of (?:arts|engineering|science)|b\.?\s*(?:a|eng|sc)\.?)$/i,
    translation: 'درجة البكالوريوس',
  },
  {
    pattern:
      /^(?:master(?:'s)?(?: degree)?|master of (?:arts|engineering|science)|m\.?\s*(?:a|eng|sc)\.?)$/i,
    translation: 'درجة الماجستير',
  },
  {
    pattern:
      /^(?:doctorate|doctoral degree|doctor of philosophy|ph\.?\s*d\.?)$/i,
    translation: 'درجة الدكتوراه',
  },
  {
    pattern: /^(?:diploma|diploma degree)$/i,
    translation: 'دبلوم',
  },
  {
    pattern: /^(?:high school diploma|secondary school certificate)$/i,
    translation: 'شهادة الثانوية العامة',
  },
];

export function isPredominantlyArabic(value: string): boolean {
  const arabicCharacters = value.match(ARABIC_CHARACTER_PATTERN)?.length ?? 0;
  const latinCharacters = value.match(LATIN_CHARACTER_PATTERN)?.length ?? 0;

  return arabicCharacters > 0 && arabicCharacters >= latinCharacters;
}

/**
 * Repairs common English qualification labels when the user's source text is
 * Arabic. The model still owns semantic translation; this deterministic layer
 * prevents a familiar fallback label from creating a mixed-language field.
 */
export function localizeEducationDegreeForSource(
  degree: string,
  sourceText: string,
): string {
  const trimmedDegree = degree.trim();
  if (
    !trimmedDegree ||
    !isPredominantlyArabic(sourceText) ||
    isPredominantlyArabic(trimmedDegree)
  ) {
    return trimmedDegree;
  }

  const normalizedDegree = trimmedDegree.replace(/[\u2018\u2019]/g, "'");
  const match = ARABIC_DEGREE_LABELS.find(({ pattern }) =>
    pattern.test(normalizedDegree),
  );

  return match?.translation ?? trimmedDegree;
}

/**
 * CVEducation stores the qualification and field of study in one label. Join
 * them in the source language instead of hard-coding the English word "in".
 */
export function formatEducationDegreeForSource(
  degree: string,
  fieldOfStudy: string,
  sourceText: string,
): string {
  const field = fieldOfStudy.trim();
  let degreeLabel = degree.trim();

  if (degreeLabel && field) {
    const englishFieldSuffix = new RegExp(
      `\\s+in\\s+${escapeRegExp(field)}$`,
      'i',
    );
    degreeLabel = degreeLabel.replace(englishFieldSuffix, '').trim();
  }

  degreeLabel = localizeEducationDegreeForSource(degreeLabel, sourceText);

  if (!degreeLabel) return field;
  if (!field) return degreeLabel;
  if (degreeLabel.toLocaleLowerCase().includes(field.toLocaleLowerCase())) {
    return degreeLabel;
  }

  const connector = isPredominantlyArabic(sourceText) ? ' في ' : ' in ';
  return `${degreeLabel}${connector}${field}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
